const POST_AUTH_REDIRECT_PATH_KEY = 'post_auth_redirect_path';

const AUTH_ROUTE_PATHS = new Set(['/sign-in', '/sign-up', '/manager/sign-in']);

const getSessionStorage = (): Storage | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.sessionStorage;
};

const normalizeRedirectPath = (path?: string | null): string | null => {
  if (typeof path !== 'string') {
    return null;
  }

  const trimmedPath = path.trim();
  return trimmedPath || null;
};

const reportStorageError = (operation: string, error: unknown): void => {
  console.error(`authRedirect: failed to ${operation}`, error);
};

export const clearRedirectPath = (): void => {
  try {
    getSessionStorage()?.removeItem(POST_AUTH_REDIRECT_PATH_KEY);
  } catch (error) {
    reportStorageError('clear redirect path from sessionStorage', error);
  }
};

export const isValidRedirectPath = (path: string): boolean => {
  const normalizedPath = normalizeRedirectPath(path);

  if (!normalizedPath || !normalizedPath.startsWith('/') || normalizedPath.startsWith('//')) {
    return false;
  }

  if (/^https?:\/\//i.test(normalizedPath)) {
    return false;
  }

  const pathname = normalizedPath.split('?')[0]?.split('#')[0];

  return !AUTH_ROUTE_PATHS.has(pathname || '');
};

export const saveRedirectPath = (path: string): void => {
  if (!isValidRedirectPath(path)) {
    clearRedirectPath();
    return;
  }

  try {
    getSessionStorage()?.setItem(POST_AUTH_REDIRECT_PATH_KEY, path);
  } catch (error) {
    reportStorageError('save redirect path to sessionStorage', error);
  }
};

export const getRedirectPath = (): string | null => {
  try {
    const savedPath = normalizeRedirectPath(
      getSessionStorage()?.getItem(POST_AUTH_REDIRECT_PATH_KEY) ?? null
    );

    if (!savedPath) {
      clearRedirectPath();
      return null;
    }

    if (!isValidRedirectPath(savedPath)) {
      clearRedirectPath();
      return null;
    }

    return savedPath;
  } catch (error) {
    reportStorageError('read redirect path from sessionStorage', error);
    clearRedirectPath();
    return null;
  }
};

export const resolveLocationRedirect = (from?: unknown): string | null => {
  return typeof from === 'string' && isValidRedirectPath(from) ? from : null;
};

export const syncLocationRedirectToStorage = (from?: unknown): string | null => {
  const locationRedirect = resolveLocationRedirect(from);

  if (!locationRedirect) {
    return getRedirectPath();
  }

  const savedRedirect = getRedirectPath();

  if (!savedRedirect) {
    saveRedirectPath(locationRedirect);
    return locationRedirect;
  }

  return savedRedirect;
};
